import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi, type AdminModelListItem } from '../lib/api';
import { isSuperAdmin } from '../lib/auth';
import { copy } from '../lib/copy';
import { ErrorState, LoadingState, PageHeader } from '../components/ui';
import { ProviderKeysPanel } from '../components/ProviderKeysPanel';

type ModelProduct = 'app' | 'studio';

type AppCategoryFilter =
  | 'all'
  | 'text'
  | 'code'
  | 'image'
  | 'video'
  | 'audio'
  | 'embedding';

type VisibilityFilter = 'all' | 'in_app' | 'in_studio' | 'unassigned' | 'active' | 'inactive';

type ProviderFilter = 'any' | 'openrouter';

const APP_CATEGORY_FILTERS: { id: AppCategoryFilter; label: string }[] = [
  { id: 'all', label: copy.models.filterAll },
  { id: 'text', label: copy.models.filterText },
  { id: 'code', label: copy.models.filterCode },
  { id: 'image', label: copy.models.filterImage },
  { id: 'video', label: copy.models.filterVideo },
  { id: 'audio', label: copy.models.filterAudio },
  { id: 'embedding', label: copy.models.filterEmbedding },
];

const VISIBILITY_FILTERS: { id: VisibilityFilter; label: string }[] = [
  { id: 'all', label: copy.models.filterAll },
  { id: 'in_app', label: copy.models.filterInApp },
  { id: 'in_studio', label: copy.models.filterInStudio },
  { id: 'unassigned', label: copy.models.filterUnassigned },
  { id: 'active', label: copy.models.filterActive },
  { id: 'inactive', label: copy.models.filterInactive },
];

const PROVIDER_FILTERS: { id: ProviderFilter; label: string }[] = [
  { id: 'any', label: copy.models.filterProviderAny },
  { id: 'openrouter', label: copy.models.openrouter },
];

function formatActiveProvider(model: AdminModelListItem): string {
  if (model.routing_note) return model.routing_note;
  if (model.active_provider === 'openrouter') return copy.models.providerOpenrouter;
  return copy.models.providerNone;
}

function categoryLabel(model: AdminModelListItem, product: ModelProduct): string {
  if (product === 'studio' && model.studio_kind) {
    switch (model.studio_kind) {
      case 'image':
        return copy.models.studioKindImage;
      case 'video':
        return copy.models.studioKindVideo;
      case 'lipsync':
        return copy.models.studioKindLipsync;
      case 'tts':
        return copy.models.studioKindTts;
      default:
        return model.studio_kind;
    }
  }

  switch (model.category) {
    case 'text':
      return copy.models.filterText;
    case 'code':
      return copy.models.filterCode;
    case 'image':
      return copy.models.filterImage;
    case 'video':
      return copy.models.filterVideo;
    case 'audio':
      return copy.models.filterAudio;
    case 'embedding':
      return copy.models.filterEmbedding;
    default:
      return model.category;
  }
}

function formatPriceValue(value: number | null | undefined): string {
  if (value == null || value < 0) return copy.models.priceUnknown;
  if (value >= 100) return value.toLocaleString('ru-RU', { maximumFractionDigits: 0 });
  return value.toLocaleString('ru-RU', { maximumFractionDigits: 2 });
}

function formatProviderPrices(info: AdminModelListItem['openrouter']): string {
  if (!info.available) return '';
  if (info.is_free) return 'FREE';
  const symbol = info.currency === 'USD' ? '$' : '';
  const suffix =
    info.price_unit.endsWith('_per_image')
      ? info.currency === 'USD'
        ? '/изображение'
        : ` ${copy.models.pricePerImage}`
      : info.price_unit.endsWith('_per_second')
        ? info.currency === 'USD'
          ? '/сек'
          : ' ₽/сек'
        : info.price_unit.endsWith('_per_request')
          ? info.currency === 'USD'
            ? '/запрос'
            : ' ₽/запрос'
          : info.currency === 'USD'
            ? '/1M'
            : ` ${copy.models.pricePerMtok}`;
  const price = (value: number) => `${symbol}${formatPriceValue(value)}${suffix}`;
  const parts: string[] = [];
  if (info.price_in != null && info.price_in >= 0) {
    parts.push(`${copy.models.priceIn}: ${price(info.price_in)}`);
  }
  if (info.price_out != null && info.price_out >= 0) {
    parts.push(`${copy.models.priceOut}: ${price(info.price_out)}`);
  }
  if (info.price_image_output != null) {
    parts.push(`изобр. выход: ${price(info.price_image_output)}`);
  }
  if (info.price_cache != null) {
    parts.push(`кэш: ${price(info.price_cache)}`);
  }
  return parts.join(' · ') || copy.models.priceUnknown;
}

function ProviderToggle({
  model,
  provider,
  checked,
  disabled,
  onChange,
}: {
  model: AdminModelListItem;
  provider: 'openrouter';
  checked: boolean;
  disabled: boolean;
  onChange: (enabled: boolean) => void;
}) {
  void provider;
  const info = model.openrouter;
  const label = copy.models.openrouter;

  return (
    <div className="space-y-1">
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled || !info.available}
          onChange={(event) => onChange(event.target.checked)}
        />
        <span className="text-ops-text">{label}</span>
      </label>
      <p className="pl-6 text-xs text-ops-muted">
        {info.available ? info.api_slug : copy.models.unavailable}
      </p>
      {info.available ? (
        <>
          <p className="pl-6 text-xs text-ops-muted">{formatProviderPrices(info)}</p>
          {info.variants?.length ? (
            <p className="pl-6 text-xs text-ops-muted">
              {info.variants
                .map((variant) =>
                  variant.perImage != null
                    ? `${variant.label}: $${variant.perImage}`
                    : `${variant.label}: ×${variant.multiplier ?? 1}`,
                )
                .join(' · ')}
            </p>
          ) : null}
          {info.source_type ? (
            <p className="pl-6 text-[11px] text-ops-muted/80">
              {info.source_type}
              {info.verified_at ? ` · ${info.verified_at}` : ''}
            </p>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

function ProductToggle({
  label,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  checked: boolean;
  disabled: boolean;
  onChange: (enabled: boolean) => void;
}) {
  return (
    <label className="inline-flex items-center text-sm" title={label}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        aria-label={label}
        onChange={(event) => onChange(event.target.checked)}
      />
    </label>
  );
}

function LocalProviderNote() {
  return <p className="text-sm text-ops-muted">{copy.models.routingLocal}</p>;
}

function filterAdminModels(
  items: AdminModelListItem[],
  filters: {
    category: AppCategoryFilter;
    visibility: VisibilityFilter;
    provider: ProviderFilter;
    query: string;
  },
): AdminModelListItem[] {
  const needle = filters.query.trim().toLowerCase();
  return items.filter((item) => {
    if (filters.category !== 'all') {
      const matchesCategory =
        item.category === filters.category || item.studio_kind === filters.category;
      if (!matchesCategory) return false;
    }
    if (filters.provider !== 'any') {
      const slugKey = item.openrouter.api_slug;
      if (!slugKey) return false;
    }
    if (filters.visibility === 'in_app' && !item.app_enabled) return false;
    if (filters.visibility === 'in_studio' && !item.studio_enabled) return false;
    if (filters.visibility === 'unassigned' && (item.app_enabled || item.studio_enabled)) {
      return false;
    }
    if (filters.visibility === 'active' && !item.is_active) return false;
    if (filters.visibility === 'inactive' && item.is_active) return false;
    if (needle) {
      return (
        item.id.toLowerCase().includes(needle) ||
        item.display_name.toLowerCase().includes(needle)
      );
    }
    return true;
  });
}

export function ModelsPage({ product }: { product: ModelProduct }) {
  const queryClient = useQueryClient();
  const canEdit = isSuperAdmin();
  const [category, setCategory] = useState<AppCategoryFilter>('all');
  const [visibility, setVisibility] = useState<VisibilityFilter>('all');
  const [provider, setProvider] = useState<ProviderFilter>('any');
  const [query, setQuery] = useState('');

  const pageTitle = product === 'app' ? copy.models.titleApp : copy.models.titleStudio;
  const pageDescription =
    product === 'app' ? copy.models.descriptionApp : copy.models.descriptionStudio;
  const categoryFilters = APP_CATEGORY_FILTERS;

  const listParams = useMemo(() => ({ product }), [product]);

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['models', listParams],
    queryFn: () => adminApi.models(listParams),
    staleTime: 60_000,
  });

  const allItems = data?.items ?? [];
  const items = useMemo(
    () =>
      filterAdminModels(allItems, {
        category,
        visibility,
        provider,
        query,
      }),
    [allItems, category, provider, query, visibility],
  );

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: {
        openrouter_enabled?: boolean;
        app_enabled?: boolean;
        studio_enabled?: boolean;
        is_active?: boolean;
      };
    }) => adminApi.updateModel(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['models'] });
    },
  });

  const handleToggle = (
    model: AdminModelListItem,
    field:
      | 'openrouter_enabled'
      | 'app_enabled'
      | 'studio_enabled'
      | 'is_active',
    enabled: boolean,
  ) => {
    updateMutation.mutate({
      id: model.id,
      payload: { [field]: enabled },
    });
  };

  const providerEnv = data?.provider_env;
  const openrouterConfigured = providerEnv?.openrouter_env_configured ?? false;
  const isLocalModel = (model: AdminModelListItem) => Boolean(model.routing_note);

  return (
    <div>
      <PageHeader title={pageTitle} description={pageDescription} />
      {product === 'app' ? <ProviderKeysPanel /> : null}

      {!canEdit ? (
        <p className="mb-4 text-sm text-ops-muted">{copy.models.readOnly}</p>
      ) : null}

      {!openrouterConfigured && providerEnv ? (
        <p className="mb-4 text-sm text-ops-muted">{copy.models.openrouterNotConfigured}</p>
      ) : null}

      <div className="mb-4 space-y-3">
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={copy.models.searchPlaceholder}
          className="w-full max-w-md rounded-lg border border-ops-border bg-ops-surface px-3 py-2 text-sm text-ops-text"
        />
        <div className="flex flex-wrap gap-2">
          {categoryFilters.map((filter) => (
            <button
              key={filter.id}
              type="button"
              className={`rounded-full border px-3 py-1 text-sm ${
                category === filter.id
                  ? 'border-ops-accent bg-ops-accent-soft text-ops-text'
                  : 'border-ops-border text-ops-muted'
              }`}
              onClick={() => setCategory(filter.id)}
            >
              {filter.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          {VISIBILITY_FILTERS.map((filter) => (
            <button
              key={filter.id}
              type="button"
              className={`rounded-full border px-3 py-1 text-sm ${
                visibility === filter.id
                  ? 'border-ops-accent bg-ops-accent-soft text-ops-text'
                  : 'border-ops-border text-ops-muted'
              }`}
              onClick={() => setVisibility(filter.id)}
            >
              {filter.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-ops-muted">{copy.models.filterProvider}:</span>
          {PROVIDER_FILTERS.map((filter) => (
            <button
              key={filter.id}
              type="button"
              className={`rounded-full border px-3 py-1 text-sm ${
                provider === filter.id
                  ? 'border-ops-accent bg-ops-accent-soft text-ops-text'
                  : 'border-ops-border text-ops-muted'
              }`}
              onClick={() => setProvider(filter.id)}
            >
              {filter.label}
            </button>
          ))}
        </div>
        <p className="text-sm text-ops-muted">
          {isLoading
            ? copy.common.loading
            : copy.models.filteredCount(items.length, allItems.length)}
          {isFetching && !isLoading ? ` · ${copy.models.refreshing}` : null}
        </p>
      </div>

      {isLoading ? <LoadingState label={copy.common.loading} /> : null}
      {isError ? (
        <ErrorState message={copy.errors.loadModels} onRetry={() => refetch()} />
      ) : null}
      {updateMutation.isError ? (
        <div className="mb-4 rounded-lg border border-ops-danger/20 bg-ops-danger-soft px-4 py-3 text-sm text-ops-danger">
          {copy.errors.updateModel}
        </div>
      ) : null}

      {data ? (
        <div className="ops-card overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="ops-table">
              <thead>
                <tr>
                  <th>{copy.models.model}</th>
                  <th>{copy.models.category}</th>
                  <th>{copy.models.tier}</th>
                  <th>{product === 'app' ? copy.models.columnApp : copy.models.columnStudio}</th>
                  <th>{copy.models.active}</th>
                  <th>{copy.models.route}</th>
                  <th>{copy.models.openrouter}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((model) => {
                  const rowBusy =
                    isFetching ||
                    (updateMutation.isPending && updateMutation.variables?.id === model.id);
                  const localOnly = isLocalModel(model);

                  return (
                    <tr key={model.id}>
                      <td>
                        <p className="font-medium text-ops-text">{model.display_name}</p>
                        <p className="text-xs text-ops-muted">{model.id}</p>
                        {product === 'studio' ? (
                          <Link
                            to={`/${product}/models/${encodeURIComponent(model.id)}/edit`}
                            className="mt-1 inline-block text-xs text-ops-accent hover:underline"
                          >
                            {copy.models.editStudio}
                          </Link>
                        ) : null}
                      </td>
                      <td className="text-sm">{categoryLabel(model, product)}</td>
                      <td className="text-sm capitalize">{model.tier}</td>
                      <td>
                        <ProductToggle
                          label={product === 'app' ? copy.models.columnApp : copy.models.columnStudio}
                          checked={product === 'app' ? model.app_enabled : model.studio_enabled}
                          disabled={!canEdit || rowBusy}
                          onChange={(enabled) =>
                            handleToggle(
                              model,
                              product === 'app' ? 'app_enabled' : 'studio_enabled',
                              enabled,
                            )
                          }
                        />
                      </td>
                      <td>
                        <ProductToggle
                          label={copy.models.active}
                          checked={model.is_active}
                          disabled={!canEdit || rowBusy}
                          onChange={(enabled) => handleToggle(model, 'is_active', enabled)}
                        />
                      </td>
                      <td className="text-sm">{formatActiveProvider(model)}</td>
                      <td>
                        {localOnly ? (
                          <LocalProviderNote />
                        ) : (
                          <ProviderToggle
                            model={model}
                            provider="openrouter"
                            checked={model.openrouter.enabled}
                            disabled={!canEdit || rowBusy}
                            onChange={(enabled) => handleToggle(model, 'openrouter_enabled', enabled)}
                          />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
}
